import { useCallback, useEffect, useMemo, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import * as Sentry from '@sentry/capacitor';
import { useMapInstance } from 'shared/map-exports';
import type { TimelinePlayback } from 'features/mapping/mapping.types';
import { useTranslation } from 'react-i18next';
import { useTerrierRadar } from 'libs/terrier';
import type { TerrierRadarConfig } from 'libs/terrier';
import { BELOW_RADAR_LAYER_ID } from 'features/mapping/styles';

type UseRadarTimelineOptions = {
  visible: boolean;
  /** Called after a Terrier error has been reported and shown to the user. */
  onError?: () => void;
};

const RADAR_TRANSITION_TIME = -1 * 2 * 60; // 2 minutes back to allow transition from observed to advection

const PLAYBACK_DURATION_SEC = 10.0;
const APPROXIMATE_FRAMES_PER_SECOND = 10;

// Target number of frames to render
const HISTORICAL_FRAMES_TARGET = Math.round(
  PLAYBACK_DURATION_SEC * 0.75 * APPROXIMATE_FRAMES_PER_SECOND,
);
const ADVECTION_FRAMES_TARGET = Math.round(
  PLAYBACK_DURATION_SEC * 0.25 * APPROXIMATE_FRAMES_PER_SECOND,
);

const RADAR_CONFIG: TerrierRadarConfig = {
  opacity: 0.5,
  animationPeriod: PLAYBACK_DURATION_SEC,
  refreshIntervalMs: 90_000,
  maskOpacity: 0.3,
  sources: [
    // historical
    {
      config: {
        source: ['mrms'],
        region: ['conus', 'hawaii', 'alaska'],
        product: ['mcr'],
        variable: 'reflectivity',
      },
      cadence: [-1 * 90 * 60, RADAR_TRANSITION_TIME, HISTORICAL_FRAMES_TARGET],
      enableForRange: [false, false],
    },
    // advection / future
    {
      config: {
        source: ['mrms'],
        region: ['conus', 'hawaii', 'alaska'],
        product: ['mcr'],
        variable: 'reflectivity_advected',
      },
      cadence: [RADAR_TRANSITION_TIME, 30 * 60, ADVECTION_FRAMES_TARGET],
      enableForRange: [true, false],
    },
  ],
};

/**
 * Radar timeline layer. Terrier draws radar on the map; this returns its
 * playback for the timeline overlay.
 */
export const useRadarTimeline = ({
  visible,
  onError,
}: UseRadarTimelineOptions): TimelinePlayback => {
  const { t } = useTranslation();
  const mapRef = useMapInstance();
  const map = useMemo(() => mapRef?.getMap() ?? null, [mapRef]);

  // When radar was last enabled, for time-to-failure in error reports
  const enabledAtRef = useRef<number | null>(null);
  useEffect(() => {
    enabledAtRef.current = visible ? Date.now() : null;
  }, [visible]);

  const handleError = useCallback(
    (error: Error): void => {
      const enabledDurationSec =
        enabledAtRef.current !== null
          ? Math.round((Date.now() - enabledAtRef.current) / 1000)
          : null;
      Sentry.captureException(error, {
        extra: {
          context: 'radar-terrier-error',
          radarEnabledDurationSec: enabledDurationSec,
        },
      });
      enqueueSnackbar(t('map.layers.radar.loadError'), { variant: 'error' });
      onError?.();
    },
    [onError, t],
  );

  return useTerrierRadar({
    config: RADAR_CONFIG,
    map,
    visible,
    belowLayer: BELOW_RADAR_LAYER_ID,
    onError: handleError,
  });
};
